import { createPortal } from 'react-dom'
import { useEffect, useRef } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { useFocusTrap } from '@hooks/useFocusTrap'
import { useEscapeKey } from '@hooks/useEscapeKey'
import { useAutoFocus } from '@hooks/useAutoFocus'
import { useClickOutside } from '@hooks/useClickOutside'
import { cx } from '@utils/cx'
import { Instagram } from '../Icons'
import styles from './ProjectModal.module.css'

export default function ProjectModal ({ project, onClose }) {
  const modalRef = useRef(null)
  const messageRef = useRef(null)

  const { t, tFunc } = useLanguage()

  useFocusTrap(modalRef)
  useEscapeKey(onClose, !!project)
  useAutoFocus(messageRef, !!project)

  const handleOverlayClick = useClickOutside(onClose)

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  const handleSocialClick = (platform, username) => {
    const url = `https://${platform}.com/${username}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleWebsiteClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  if (!project) return null

  return createPortal(
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.projectModal} role='dialog' aria-modal='true' aria-labelledby='modal-title' ref={modalRef}>
        {/* Header */}
        <header className={styles.header}>
          <h2
            id='modal-title'
            className={styles.title}
            ref={messageRef}
            tabIndex='-1'
          >
            {project.name}
          </h2>
          <button
            className={styles.close}
            onClick={onClose}
            aria-label={t.about.access.projects.close}
            type='button'
          >
            ✕
          </button>
        </header>

        {/* Content */}
        <div className={styles.content}>
          {/* Logo */}
          <div className={styles.logoContainer}>
            <img
              src={project.logo}
              alt={`Logo de ${project.name}`}
              className={styles.logo}
              loading='lazy'
            />
          </div>

          {/* Description */}
          <div className={styles.description}>
            <p className={styles.text}>
              {tFunc(project.description)}
            </p>
          </div>

          {/* Links */}
          <div className={styles.links}>
            {project.url && (
              <button
                className={cx(
                  styles.link,
                  styles.linkWebsite
                )}
                onClick={() => handleWebsiteClick(project.url)}
                type='button'
                aria-label={t.about.access.projects.visit.replace('{ name }', project.name)}
              >
                <span className={styles.linkIcon}>🌐</span>
                <span className={styles.linkText}>{t.about.projects.modal.visit}</span>
              </button>
            )}

            {project.social?.ig && (
              <button
                className={cx(styles.link, styles.linkInstagram)}
                onClick={() => handleSocialClick('instagram', project.social.ig)}
                type='button'
                aria-label={t.about.access.projects.instagram.replace('{ name }', project.name)}
              >
                <span className={styles.linkIcon}>
                  <Instagram size={18} stroke={1.5} aria-hidden='true' />
                </span>
                <span className={styles.linkText}>@{project.social.ig}</span>
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className={styles.footer}>
          <p className={styles.footerText}>
            {t.about.projects.modal.disclaimer}
          </p>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            type='button'
          >
            {t.core.common.close}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  )
}
